import React from 'react';
import PropTypes from 'prop-types';
import { Container } from 'semantic-ui-react';
import TeaserImages from '../Helpers/TeaserImages';
import RelatedItemsDisplay from '../Helpers/RelatedItemsDisplay';
import Sections from '../Helpers/Sections';
import DefaultViewWithoutTitle from '../../Custom/DefaultViewWithoutTitle';
import { hasImage } from '../../../helpers/content';

const ExternalProjectView = (props) => {
  const { content } = props;

  const hasLeadImage = hasImage(content.image);
  const hasLink = !!content.link_field;

  const hasProjects =
    content.related_projects?.length > 0 && !content.hide_related_projects;
  const hasTopics =
    content.related_topics?.length > 0 && !content.hide_related_topics;
  const hasResearchers =
    content.related_researchers?.length > 0 &&
    !content.hide_related_researchers;

  // Each section's own leading divider only makes sense if something was
  // already rendered above it - see the same pattern in ProjectView/TopicView.

  return (
    <Container id="view-wrapper" className="ExternalProjectView">
      {/* Header - title + description, same as shown on the listing card */}
      <div className="view-header">
        <h2 className="view-header__title">{content.title || content.id}</h2>
        {/* No description here. `description` is a listing field - the one
            line that goes on a card and into a search result - and repeating
            it as the first paragraph of the page it summarises says the same
            thing twice. The full view has its own content. */}
        <div className="view-header__divider" />
      </div>
      <Sections>
        {/* Lead Image */}
        {hasLeadImage && (
          <TeaserImages
            image1={content.image}
            caption1={content.image_caption}
            copyright1={content.image_copyright}
            copyright1BlackFont={content.image_copyright_black_font}
            alt={content.title}
          />
        )}

        {/* Additional Link */}
        {hasLink && (
          <>
            <div className="view-section">
              <div className="view-section__buttons">
                <a
                  href={content.link_field}
                  className="view-section__button"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Visit Project
                </a>
              </div>
            </div>
          </>
        )}

        {/* Related Projects */}
        {hasProjects && (
          <>
            <div className="view-section">
              <h3 className="view-section__label">Related Projects:</h3>
              <RelatedItemsDisplay
                items={content.related_projects}
                mode={content.related_projects_display}
                kind="plain"
                fieldKey={`${content['@id']}-related_projects`}
              />
            </div>
          </>
        )}

        {/* Related Topics */}
        {hasTopics && (
          <>
            <div className="view-section">
              <h3 className="view-section__label">Related Topics:</h3>
              <RelatedItemsDisplay
                items={content.related_topics}
                mode={content.related_topics_display}
                kind="plain"
                fieldKey={`${content['@id']}-related_topics`}
              />
            </div>
          </>
        )}

        {/* Related Researchers */}
        {hasResearchers && (
          <>
            <div className="view-section">
              <h3 className="view-section__label">Associated Researchers:</h3>
              <RelatedItemsDisplay
                items={content.related_researchers}
                mode={content.related_researchers_display}
                kind="person"
                fieldKey={`${content['@id']}-related_researchers`}
              />
            </div>
          </>
        )}

        {/* DefaultView for Blocks - lets editors add free-form content when
            this item gets its own linked full view (title not hidden in a
            listing). */}
      </Sections>

      <DefaultViewWithoutTitle {...props} />
    </Container>
  );
};

ExternalProjectView.propTypes = {
  content: PropTypes.shape({
    '@id': PropTypes.string,
    title: PropTypes.string,
    id: PropTypes.string,
    description: PropTypes.string,
    image: PropTypes.object,
    image_caption: PropTypes.string,
    image_copyright: PropTypes.string,
    image_copyright_black_font: PropTypes.bool,
    link_field: PropTypes.string,
    hide_related_projects: PropTypes.bool,
    hide_related_topics: PropTypes.bool,
    hide_related_researchers: PropTypes.bool,
    related_projects_display: PropTypes.string,
    related_topics_display: PropTypes.string,
    related_researchers_display: PropTypes.string,
    related_projects: PropTypes.arrayOf(
      PropTypes.shape({ '@id': PropTypes.string, title: PropTypes.string }),
    ),
    related_topics: PropTypes.arrayOf(
      PropTypes.shape({ '@id': PropTypes.string, title: PropTypes.string }),
    ),
    related_researchers: PropTypes.arrayOf(
      PropTypes.shape({ '@id': PropTypes.string, title: PropTypes.string }),
    ),
  }).isRequired,
};

export default ExternalProjectView;
